<template>
  <v-card class="pl-5 pr-5 pb-5">
    <v-row>
      <v-toolbar flat>
        <div>
          <div class="headline pl-1">Marketing Sources</div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedOfficeId"
          :items="officeItems"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 220px"
          class="mr-4"
        />
        <v-menu
          v-model="monthMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-btn text v-on="on">{{ displayMonth }}</v-btn>
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @input="monthMenu = false"
          />
        </v-menu>
      </v-toolbar>
    </v-row>

    <v-progress-linear v-if="$apollo.loading" indeterminate color="primary" />

    <div>
      <!-- Summary Cards -->
      <v-row>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Total Retained</div>
            <div class="text-h4">{{ totalRetained }}</div>
            <div v-if="totalGoal" class="caption">
              Goal: {{ totalGoal }}
              <span v-if="isCurrentMonth">
                &middot; Target: {{ proratedTotalTarget }}</span
              >
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Total Spend</div>
            <div class="text-h4">{{ formatCurrency(totalSpend) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Avg Cost Per Case</div>
            <div class="text-h4">{{ formatCurrency(avgCostPerCase) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Goal Progress</div>
            <div v-if="totalGoal" class="text-h4">{{ goalProgressPct }}%</div>
            <v-progress-linear
              v-if="totalGoal"
              :value="goalProgressPct"
              :color="goalProgressColor"
              height="8"
              class="mt-2"
            />
            <div v-if="!totalGoal" class="text-h4 grey--text">--</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Source Performance Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Source Performance
              <span
                v-if="selectedOfficeName"
                class="ml-2 subtitle-1 grey--text"
              >
                ({{ selectedOfficeName }})
              </span>
              <v-spacer />
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                class="shrink"
              />
            </v-card-title>
            <v-data-table
              :headers="tableHeaders"
              :items="sourceRows"
              :search="search"
              :custom-filter="filterRow"
              :sort-by="['retained']"
              :sort-desc="[true]"
              :loading="$apollo.loading"
              item-key="rowKey"
              show-expand
              :expanded.sync="expanded"
              :item-class="rowClass"
              dense
            >
              <template
                v-slot:item.data-table-expand="{ item, isExpanded, expand }"
              >
                <v-icon
                  v-if="item.isGroup"
                  small
                  @click.stop="expand(!isExpanded)"
                >
                  {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </template>
              <template v-slot:item.sourceName="{ item }">
                {{ item.sourceName }}
                <v-chip
                  v-if="item.isGroup"
                  x-small
                  class="ml-2"
                  title="Number of sources in this group"
                >
                  {{ item.members.length }}
                </v-chip>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="item.statusColor" small dark>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.goal="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.editGoal"
                  @save="saveGoal(item)"
                  large
                >
                  {{ item.goal || "-" }}
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.editGoal"
                      :label="item.isGroup ? 'Group goal' : 'Goal'"
                      type="number"
                      single-line
                      :hint="groupAmountHint(item, 'goal')"
                      :persistent-hint="item.isGroup"
                    />
                  </template>
                </v-edit-dialog>
              </template>
              <template v-slot:item.target="{ item }">
                <span v-if="item.target != null">{{ item.target }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:item.spend="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.editSpend"
                  @save="saveSpend(item)"
                  large
                >
                  {{ item.spend ? formatCurrency(item.spend) : "-" }}
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.editSpend"
                      :label="item.isGroup ? 'Group spend' : 'Spend'"
                      type="number"
                      prefix="$"
                      single-line
                      :hint="groupAmountHint(item, 'spend')"
                      :persistent-hint="item.isGroup"
                    />
                  </template>
                </v-edit-dialog>
              </template>
              <template v-slot:item.costPerCase="{ item }">
                {{ item.costPerCase ? formatCurrency(item.costPerCase) : "-" }}
              </template>
              <template v-slot:header.averageFee="{ header }">
                <span
                  title="Average attorney fee from Auto cases fully settled in the preceding 2 years, excluding fees over $1M"
                  >{{ header.text }}</span
                >
              </template>
              <template v-slot:item.averageFee="{ item }">
                {{ item.averageFee ? formatCurrency(item.averageFee) : "-" }}
              </template>
              <template v-slot:header.expenseRatio="{ header }">
                <span
                  title="Cost per case divided by average fee, when spend is entered"
                  >{{ header.text }}</span
                >
              </template>
              <template v-slot:item.expenseRatio="{ item }">
                {{
                  item.expenseRatio != null
                    ? formatExpenseRatio(item.expenseRatio)
                    : "-"
                }}
              </template>
              <template v-slot:item.trailing6Avg="{ item }">
                {{ item.trailing6Avg ? item.trailing6Avg.toFixed(1) : "-" }}
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="py-2 px-0">
                  <v-data-table
                    v-if="item.isGroup"
                    :headers="memberTableHeaders"
                    :items="item.members"
                    dense
                    hide-default-footer
                    :items-per-page="-1"
                    item-key="rowKey"
                    class="nested-sources-table"
                  >
                    <template v-slot:item.status="{ item: member }">
                      <v-chip :color="member.statusColor" small dark>
                        {{ member.status }}
                      </v-chip>
                    </template>
                    <template v-slot:item.goal="{ item: member }">
                      <v-edit-dialog
                        :return-value.sync="member.editGoal"
                        @save="saveGoal(member)"
                        large
                      >
                        {{ member.goal || "-" }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="member.editGoal"
                            label="Goal"
                            type="number"
                            single-line
                          />
                        </template>
                      </v-edit-dialog>
                    </template>
                    <template v-slot:item.target="{ item: member }">
                      <span v-if="member.target != null">{{
                        member.target
                      }}</span>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:item.spend="{ item: member }">
                      <v-edit-dialog
                        :return-value.sync="member.editSpend"
                        @save="saveSpend(member)"
                        large
                      >
                        {{ member.spend ? formatCurrency(member.spend) : "-" }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="member.editSpend"
                            label="Spend"
                            type="number"
                            prefix="$"
                            single-line
                          />
                        </template>
                      </v-edit-dialog>
                    </template>
                    <template v-slot:item.costPerCase="{ item: member }">
                      {{
                        member.costPerCase
                          ? formatCurrency(member.costPerCase)
                          : "-"
                      }}
                    </template>
                    <template v-slot:item.averageFee="{ item: member }">
                      {{
                        member.averageFee
                          ? formatCurrency(member.averageFee)
                          : "-"
                      }}
                    </template>
                    <template v-slot:item.expenseRatio="{ item: member }">
                      {{
                        member.expenseRatio != null
                          ? formatExpenseRatio(member.expenseRatio)
                          : "-"
                      }}
                    </template>
                    <template v-slot:item.trailing6Avg="{ item: member }">
                      {{
                        member.trailing6Avg
                          ? member.trailing6Avg.toFixed(1)
                          : "-"
                      }}
                    </template>
                  </v-data-table>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Manage Source Groups -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>Manage Source Groups</v-card-title>
            <v-card-text>
              <v-alert
                v-if="groupError"
                type="error"
                dense
                dismissible
                class="mb-4"
                @input="groupError = null"
              >
                {{ groupError }}
              </v-alert>
              <v-row align="center">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="groupForm.name"
                    label="Group name"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="5">
                  <v-autocomplete
                    v-model="groupForm.members"
                    :items="availableGroupMembers"
                    label="Sources in this group"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn
                    color="primary"
                    :disabled="!groupForm.name"
                    @click="saveGroup"
                  >
                    {{ groupForm.id ? "Update Group" : "Create Group" }}
                  </v-btn>
                  <v-btn
                    v-if="groupForm.id"
                    text
                    class="ml-2"
                    @click="resetGroupForm"
                  >
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
              <v-data-table
                v-if="marketingSourceGroups && marketingSourceGroups.length > 0"
                :headers="groupTableHeaders"
                :items="marketingSourceGroups"
                dense
                class="mt-4"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.members="{ item }">
                  {{ (item.members || []).join(", ") || "—" }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click="editGroup(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="deleteGroup(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              <div v-else class="text-center pa-4 grey--text">
                No source groups yet. Group related sources to enter spend and
                goals for the whole set, while still tracking individual sources
                inside the group.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Goals Management -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Manage Goals &amp; Spend ({{ displayMonth
              }}{{
                selectedOfficeName
                  ? " - " + selectedOfficeName
                  : " - All Offices"
              }})
            </v-card-title>
            <v-card-text>
              <!-- Overall Goal -->
              <v-row align="center">
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="overallGoalInput"
                    type="number"
                    label="Overall Monthly Retained Goal"
                    dense
                    outlined
                    @blur="saveOverallGoal"
                  />
                </v-col>
              </v-row>

              <v-divider class="mb-4" />

              <!-- Add new source goal -->
              <v-row align="center">
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="newGoalSource"
                    :items="availableSources"
                    item-text="contact"
                    item-value="contact"
                    label="Add source goal..."
                    dense
                    outlined
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="newGoalValue"
                    type="number"
                    label="Goal"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="newGoalSpend"
                    type="number"
                    label="Spend"
                    prefix="$"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn
                    color="primary"
                    :disabled="!newGoalSource || !newGoalValue"
                    @click="addSourceGoal"
                  >
                    Add Goal
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Existing goals table -->
              <v-data-table
                v-if="sourceGoalRows.length > 0"
                :headers="goalTableHeaders"
                :items="sourceGoalRows"
                dense
                class="mt-4"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.sourceName="{ item }">
                  {{ item.sourceName }}
                  <v-chip v-if="item.isGroup" x-small class="ml-1"
                    >Group</v-chip
                  >
                </template>
                <template v-slot:item.goal="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.editGoal"
                    @save="saveGoalFromManager(item)"
                    large
                  >
                    {{ item.goal }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.editGoal"
                        label="Goal"
                        type="number"
                        single-line
                      />
                    </template>
                  </v-edit-dialog>
                </template>
                <template v-slot:item.spend="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.editSpend"
                    @save="saveSpendFromManager(item)"
                    large
                  >
                    {{ item.spend ? formatCurrency(item.spend) : "-" }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.editSpend"
                        label="Spend"
                        type="number"
                        prefix="$"
                        single-line
                      />
                    </template>
                  </v-edit-dialog>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click="deleteGoal(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              <div
                v-if="sourceGoalRows.length === 0"
                class="text-center pa-4 grey--text"
              >
                No source goals set for {{ displayMonth }}. Use the form above
                to add goals.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts -->
      <v-row>
        <v-col cols="12" sm="6">
          <v-card class="pa-3">
            <SourcesTrendChart :data="retainedBySourceByMonth" />
          </v-card>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card class="pa-3">
            <SourcesGoalChart :sourceRows="sourceRowsWithGoals" />
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import {
  format,
  parseISO,
  getDaysInMonth,
  getDate,
  subYears,
  endOfMonth
} from "date-fns";
import SourcesTrendChart from "@/charts/SourcesTrendChart.vue";
import SourcesGoalChart from "@/charts/SourcesGoalChart.vue";

const OFFICES_QUERY = gql`
  query CaseOffices {
    caseOffices {
      officeId
      office
    }
  }
`;

const RETAINED_BY_SOURCE_QUERY = gql`
  query RetainedBySourceByMonth($monthsBack: Int, $officeId: Int) {
    retainedBySourceByMonth(monthsBack: $monthsBack, officeId: $officeId) {
      sourceName
      month
      year
      count
    }
  }
`;

const AVERAGE_FEES_BY_SOURCE_QUERY = gql`
  query AverageFeesBySource(
    $startDate: String
    $endDate: String
    $officeId: Int
  ) {
    averageFeesBySource(
      startDate: $startDate
      endDate: $endDate
      officeId: $officeId
    ) {
      sourceName
      averageFee
      settledCount
    }
  }
`;

const GOALS_QUERY = gql`
  query MarketingSourceGoals($month: String, $officeName: String) {
    marketingSourceGoals(month: $month, officeName: $officeName) {
      id
      sourceName
      month
      goal
      spend
      officeName
    }
  }
`;

const ADVERTISING_SOURCES_QUERY = gql`
  query AdvertisingSources {
    advertisingSources {
      contactId
      contact
    }
  }
`;

const UPSERT_GOAL = gql`
  mutation UpsertMarketingSourceGoal($goal: MarketingSourceGoalInput!) {
    upsertMarketingSourceGoal(goal: $goal) {
      id
      sourceName
      month
      goal
      spend
      officeName
    }
  }
`;

const DELETE_GOAL = gql`
  mutation DeleteMarketingSourceGoal($id: ID!) {
    deleteMarketingSourceGoal(id: $id) {
      id
    }
  }
`;

const GROUPS_QUERY = gql`
  query MarketingSourceGroups {
    marketingSourceGroups {
      id
      name
      members
    }
  }
`;

const UPSERT_GROUP = gql`
  mutation UpsertMarketingSourceGroup($group: MarketingSourceGroupInput!) {
    upsertMarketingSourceGroup(group: $group) {
      id
      name
      members
    }
  }
`;

const DELETE_GROUP = gql`
  mutation DeleteMarketingSourceGroup($id: ID!) {
    deleteMarketingSourceGroup(id: $id) {
      id
    }
  }
`;

export default {
  components: {
    SourcesTrendChart,
    SourcesGoalChart
  },
  data() {
    return {
      selectedMonth: format(new Date(), "yyyy-MM"),
      monthMenu: false,
      search: "",
      overallGoalInput: null,
      newGoalSource: null,
      newGoalValue: null,
      newGoalSpend: null,
      selectedOfficeId: null,
      expanded: [],
      groupError: null,
      groupForm: {
        id: null,
        name: "",
        members: []
      }
    };
  },
  computed: {
    officeItems() {
      const items = [{ text: "All Offices", value: null }];
      if (this.caseOffices) {
        this.caseOffices.forEach(o => {
          items.push({ text: o.office, value: o.officeId });
        });
      }
      return items;
    },
    selectedOffice() {
      if (!this.selectedOfficeId || !this.caseOffices) return null;
      return (
        this.caseOffices.find(o => o.officeId === this.selectedOfficeId) || null
      );
    },
    selectedOfficeName() {
      return this.selectedOffice ? this.selectedOffice.office : null;
    },
    currentOfficeNameForGoals() {
      return this.selectedOfficeName || "__ALL__";
    },
    displayMonth() {
      return format(parseISO(this.selectedMonth + "-01"), "MMMM yyyy");
    },
    feeStartDate() {
      return format(
        subYears(parseISO(this.selectedMonth + "-01"), 2),
        "yyyy-MM-dd"
      );
    },
    feeEndDate() {
      const monthEnd = endOfMonth(parseISO(this.selectedMonth + "-01"));
      const today = new Date();
      const end = monthEnd > today ? today : monthEnd;
      return format(end, "yyyy-MM-dd");
    },
    selectedMonthDate() {
      return this.selectedMonth + "-01";
    },
    selectedMonthNum() {
      return parseInt(this.selectedMonth.split("-")[1]);
    },
    selectedYearNum() {
      return parseInt(this.selectedMonth.split("-")[0]);
    },
    isCurrentMonth() {
      const now = new Date();
      return (
        this.selectedYearNum === now.getFullYear() &&
        this.selectedMonthNum === now.getMonth() + 1
      );
    },
    dayProgress() {
      if (!this.isCurrentMonth) return 1;
      const today = getDate(new Date());
      const daysInMonth = getDaysInMonth(new Date());
      return today / daysInMonth;
    },
    goalsMap() {
      if (!this.marketingSourceGoals) return {};
      let map = {};
      this.marketingSourceGoals.forEach(g => {
        map[g.sourceName] = g;
      });
      return map;
    },
    totalGoal() {
      const overall = this.goalsMap["__TOTAL__"];
      return overall ? overall.goal : null;
    },
    proratedTotalTarget() {
      if (!this.totalGoal) return null;
      return Math.round(this.totalGoal * this.dayProgress);
    },
    priorYearMonth() {
      return {
        month: this.selectedMonthNum,
        year: this.selectedYearNum - 1
      };
    },
    sourceData() {
      if (!this.retainedBySourceByMonth) return {};
      let map = {};
      this.retainedBySourceByMonth.forEach(row => {
        if (!row.sourceName) return;
        if (!map[row.sourceName]) map[row.sourceName] = [];
        map[row.sourceName].push(row);
      });
      return map;
    },
    feesBySource() {
      if (!this.averageFeesBySource) return {};
      let map = {};
      this.averageFeesBySource.forEach(row => {
        if (!row.sourceName) return;
        const fee = parseFloat(row.averageFee);
        map[row.sourceName] = {
          averageFee: isNaN(fee) ? null : fee,
          settledCount: row.settledCount || 0
        };
      });
      return map;
    },
    groupedSourceNames() {
      const set = {};
      (this.marketingSourceGroups || []).forEach(g => {
        (g.members || []).forEach(name => {
          set[name] = true;
        });
      });
      return set;
    },
    groupNameByKey() {
      const map = {};
      (this.marketingSourceGroups || []).forEach(g => {
        map["__GROUP__:" + g.id] = g.name;
      });
      return map;
    },
    sourceRows() {
      const groupedNames = this.groupedSourceNames;
      const groupRows = (this.marketingSourceGroups || []).map(group =>
        this.buildGroupRow(group)
      );
      const ungrouped = Object.keys(this.sourceData)
        .filter(name => !groupedNames[name])
        .map(name => this.buildSourceRow(name));
      return groupRows.concat(ungrouped);
    },
    sourceRowsWithGoals() {
      return this.sourceRows.filter(r => r.goal);
    },
    totalRetained() {
      return this.sourceRows.reduce((sum, r) => sum + r.retained, 0);
    },
    totalSpend() {
      return this.sourceRows.reduce((sum, r) => sum + (r.spend || 0), 0);
    },
    avgCostPerCase() {
      if (this.totalRetained === 0) return 0;
      return this.totalSpend / this.totalRetained;
    },
    goalProgressPct() {
      if (!this.totalGoal) return 0;
      const prorated = this.totalGoal * this.dayProgress;
      return Math.min(Math.round((this.totalRetained / prorated) * 100), 999);
    },
    goalProgressColor() {
      if (this.goalProgressPct >= 100) return "green";
      if (this.goalProgressPct >= 80) return "orange";
      return "red";
    },
    tableHeaders() {
      return [
        { text: "Source", value: "sourceName" },
        { text: "Retained", value: "retained" },
        { text: "Goal", value: "goal" },
        { text: "Target", value: "target" },
        { text: "Prior Year", value: "priorYear" },
        { text: "6mo Avg", value: "trailing6Avg" },
        { text: "Status", value: "status" },
        { text: "Spend", value: "spend" },
        { text: "Cost/Case", value: "costPerCase" },
        { text: "Avg Fee", value: "averageFee" },
        { text: "Expense Ratio", value: "expenseRatio" }
      ];
    },
    memberTableHeaders() {
      return this.tableHeaders;
    },
    availableSources() {
      if (!this.advertisingSources) return [];
      return this.advertisingSources.filter(s => s.contact);
    },
    availableGroupMembers() {
      const taken = {};
      (this.marketingSourceGroups || []).forEach(g => {
        if (this.groupForm.id && String(g.id) === String(this.groupForm.id)) {
          return;
        }
        (g.members || []).forEach(name => {
          taken[name] = true;
        });
      });
      const names = {};
      (this.advertisingSources || []).forEach(s => {
        if (s.contact) names[s.contact] = true;
      });
      (this.groupForm.members || []).forEach(name => {
        names[name] = true;
      });
      return Object.keys(names)
        .filter(name => !taken[name])
        .sort();
    },
    sourceGoalRows() {
      if (!this.marketingSourceGoals) return [];
      const groupNames = this.groupNameByKey;
      return this.marketingSourceGoals
        .filter(g => g.sourceName !== "__TOTAL__")
        .map(g => ({
          id: g.id,
          sourceName: groupNames[g.sourceName] || g.sourceName,
          goalSourceName: g.sourceName,
          isGroup: !!groupNames[g.sourceName],
          goal: g.goal,
          editGoal: g.goal,
          ownGoal: g.goal,
          spend: parseFloat(g.spend) || null,
          editSpend: parseFloat(g.spend) || null,
          ownSpend: parseFloat(g.spend) || null
        }));
    },
    goalTableHeaders() {
      return [
        { text: "Source", value: "sourceName" },
        { text: "Goal", value: "goal" },
        { text: "Spend", value: "spend" },
        { text: "", value: "actions", sortable: false, width: "50px" }
      ];
    },
    groupTableHeaders() {
      return [
        { text: "Group", value: "name" },
        { text: "Sources", value: "members" },
        { text: "", value: "actions", sortable: false, width: "90px" }
      ];
    }
  },
  watch: {
    marketingSourceGoals() {
      const overall = this.goalsMap["__TOTAL__"];
      this.overallGoalInput = overall ? overall.goal : null;
    }
  },
  methods: {
    goalPayload(overrides) {
      return Object.assign(
        { officeName: this.currentOfficeNameForGoals },
        overrides
      );
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) return "$0";
      return (
        "$" +
        Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      );
    },
    formatExpenseRatio(value) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return (Number(value) * 100).toFixed(1) + "%";
    },
    filterRow(_value, search, item) {
      if (!search) return true;
      const hay = (item.searchHaystack || item.sourceName || "").toLowerCase();
      return hay.indexOf(search.toLowerCase()) !== -1;
    },
    rowClass(item) {
      return item.isGroup ? "group-row" : "";
    },
    groupGoalKey(id) {
      return "__GROUP__:" + id;
    },
    goalSourceName(item) {
      if (item.goalSourceName) return item.goalSourceName;
      if (item.isGroup) return this.groupGoalKey(item.groupId);
      return item.sourceName;
    },
    groupAmountHint(item, kind) {
      if (!item.isGroup) return undefined;
      if (kind === "goal") {
        const n = item.memberGoalSum || 0;
        return n ? "Members add " + n : "Does not include member goals";
      }
      const n = item.memberSpendSum || 0;
      return n
        ? "Members add " + this.formatCurrency(n)
        : "Does not include member spend";
    },
    statusFor(goal, retained, trailing6Avg) {
      let status = "No Data";
      let statusColor = "grey";
      if (goal) {
        const target = goal * this.dayProgress;
        const pct = target > 0 ? (retained / target) * 100 : 0;
        if (pct >= 100) {
          status = "Exceeding";
          statusColor = "green";
        } else if (pct >= 80) {
          status = "On Track";
          statusColor = "orange";
        } else {
          status = "Lagging";
          statusColor = "red";
        }
      } else if (trailing6Avg) {
        const target = trailing6Avg * this.dayProgress;
        const pct = target > 0 ? (retained / target) * 100 : 0;
        if (pct >= 100) {
          status = "Above Avg";
          statusColor = "green";
        } else if (pct >= 80) {
          status = "Near Avg";
          statusColor = "orange";
        } else {
          status = "Below Avg";
          statusColor = "red";
        }
      }
      return { status, statusColor };
    },
    trailing6AvgForSources(sourceNames) {
      const sixMonthsAgo = new Date(
        this.selectedYearNum,
        this.selectedMonthNum - 1,
        1
      );
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const monthTotals = {};
      (sourceNames || []).forEach(name => {
        const rows = this.sourceData[name] || [];
        rows.forEach(r => {
          if (
            r.month === this.selectedMonthNum &&
            r.year === this.selectedYearNum
          ) {
            return;
          }
          const rowDate = new Date(r.year, r.month - 1, 1);
          if (rowDate < sixMonthsAgo) return;
          const key = r.year + "-" + r.month;
          monthTotals[key] = (monthTotals[key] || 0) + r.count;
        });
      });
      const values = Object.keys(monthTotals).map(k => monthTotals[k]);
      if (values.length === 0) return null;
      return values.reduce((sum, v) => sum + v, 0) / values.length;
    },
    weightedAverageFee(sourceNames) {
      let weighted = 0;
      let count = 0;
      (sourceNames || []).forEach(name => {
        const feeEntry = this.feesBySource[name];
        if (feeEntry && feeEntry.averageFee && feeEntry.settledCount) {
          weighted += feeEntry.averageFee * feeEntry.settledCount;
          count += feeEntry.settledCount;
        }
      });
      return count > 0 ? weighted / count : null;
    },
    buildSourceRow(name) {
      const rows = this.sourceData[name] || [];
      const currentRow = rows.find(
        r =>
          r.month === this.selectedMonthNum && r.year === this.selectedYearNum
      );
      const priorYearRow = rows.find(
        r =>
          r.month === this.priorYearMonth.month &&
          r.year === this.priorYearMonth.year
      );
      const trailing6Avg = this.trailing6AvgForSources([name]);
      const retained = currentRow ? currentRow.count : 0;
      const goalEntry = this.goalsMap[name];
      const goal = goalEntry ? goalEntry.goal : null;
      const spend = goalEntry ? parseFloat(goalEntry.spend) || null : null;
      const costPerCase = spend && retained > 0 ? spend / retained : null;
      const feeEntry = this.feesBySource[name];
      const averageFee = feeEntry ? feeEntry.averageFee : null;
      const expenseRatio =
        spend && costPerCase && averageFee ? costPerCase / averageFee : null;
      const statusInfo = this.statusFor(goal, retained, trailing6Avg);
      const target = goal
        ? Math.round(goal * this.dayProgress)
        : trailing6Avg
        ? Math.round(trailing6Avg * this.dayProgress)
        : null;

      return {
        rowKey: "source-" + name,
        sourceName: name,
        isGroup: false,
        members: [],
        searchHaystack: name,
        retained,
        goal,
        ownGoal: goal,
        editGoal: goal,
        target,
        priorYear: priorYearRow ? priorYearRow.count : 0,
        trailing6Avg,
        status: statusInfo.status,
        statusColor: statusInfo.statusColor,
        spend,
        ownSpend: spend,
        editSpend: spend,
        costPerCase,
        averageFee,
        expenseRatio
      };
    },
    buildGroupRow(group) {
      const memberNames = group.members || [];
      const members = memberNames.map(name => this.buildSourceRow(name));
      const retained = members.reduce((sum, m) => sum + (m.retained || 0), 0);
      const priorYear = members.reduce((sum, m) => sum + (m.priorYear || 0), 0);
      const trailing6Avg = this.trailing6AvgForSources(memberNames);
      const goalEntry = this.goalsMap[this.groupGoalKey(group.id)];
      const ownGoal = goalEntry ? goalEntry.goal : null;
      const ownSpend = goalEntry ? parseFloat(goalEntry.spend) || null : null;
      const memberGoalSum = members.reduce(
        (sum, m) => sum + (m.ownGoal || 0),
        0
      );
      const memberSpendSum = members.reduce(
        (sum, m) => sum + (m.ownSpend || 0),
        0
      );
      const hasGoal = ownGoal != null || members.some(m => m.ownGoal != null);
      const hasSpend =
        ownSpend != null || members.some(m => m.ownSpend != null);
      const goal = hasGoal ? (ownGoal || 0) + memberGoalSum : null;
      const spend = hasSpend ? (ownSpend || 0) + memberSpendSum : null;
      const costPerCase = spend && retained > 0 ? spend / retained : null;
      const averageFee = this.weightedAverageFee(memberNames);
      const expenseRatio =
        spend && costPerCase && averageFee ? costPerCase / averageFee : null;
      const statusInfo = this.statusFor(goal, retained, trailing6Avg);
      const target = goal
        ? Math.round(goal * this.dayProgress)
        : trailing6Avg
        ? Math.round(trailing6Avg * this.dayProgress)
        : null;

      return {
        rowKey: "group-" + group.id,
        sourceName: group.name,
        groupId: group.id,
        isGroup: true,
        members,
        searchHaystack: [group.name].concat(memberNames).join(" "),
        retained,
        goal,
        ownGoal,
        editGoal: ownGoal,
        memberGoalSum,
        target,
        priorYear,
        trailing6Avg,
        status: statusInfo.status,
        statusColor: statusInfo.statusColor,
        spend,
        ownSpend,
        editSpend: ownSpend,
        memberSpendSum,
        costPerCase,
        averageFee,
        expenseRatio
      };
    },
    async saveGoal(item) {
      const goalVal = parseInt(item.editGoal);
      if (isNaN(goalVal) || goalVal < 0) return;
      const ownSpend = item.ownSpend != null ? item.ownSpend : null;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.goalSourceName(item),
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: ownSpend != null ? String(ownSpend) : null
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveSpend(item) {
      const spendVal = parseFloat(item.editSpend);
      if (isNaN(spendVal) || spendVal < 0) return;
      const goal = item.ownGoal != null ? item.ownGoal : 0;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.goalSourceName(item),
            month: this.selectedMonthDate,
            goal: goal,
            spend: String(spendVal)
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveOverallGoal() {
      const goalVal = parseInt(this.overallGoalInput);
      if (isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: "__TOTAL__",
            month: this.selectedMonthDate,
            goal: goalVal
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async addSourceGoal() {
      const goalVal = parseInt(this.newGoalValue);
      if (!this.newGoalSource || isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.newGoalSource,
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: this.newGoalSpend ? String(this.newGoalSpend) : null
          })
        }
      });
      this.newGoalSource = null;
      this.newGoalValue = null;
      this.newGoalSpend = null;
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveGoalFromManager(item) {
      const goalVal = parseInt(item.editGoal);
      if (isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.goalSourceName(item),
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: item.ownSpend != null ? String(item.ownSpend) : null
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveSpendFromManager(item) {
      const spendVal = parseFloat(item.editSpend);
      if (isNaN(spendVal) || spendVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.goalSourceName(item),
            month: this.selectedMonthDate,
            goal: item.ownGoal != null ? item.ownGoal : item.goal,
            spend: String(spendVal)
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async deleteGoal(item) {
      await this.$apollo.mutate({
        mutation: DELETE_GOAL,
        variables: { id: item.id }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    resetGroupForm() {
      this.groupForm = { id: null, name: "", members: [] };
      this.groupError = null;
    },
    editGroup(item) {
      this.groupForm = {
        id: item.id,
        name: item.name,
        members: (item.members || []).slice()
      };
      this.groupError = null;
    },
    mutationErrorMessage(error) {
      if (
        error &&
        error.graphQLErrors &&
        error.graphQLErrors[0] &&
        error.graphQLErrors[0].message
      ) {
        return error.graphQLErrors[0].message;
      }
      return "Could not save group";
    },
    async saveGroup() {
      if (!this.groupForm.name) return;
      const group = {
        name: this.groupForm.name,
        members: this.groupForm.members || []
      };
      if (this.groupForm.id) group.id = this.groupForm.id;
      try {
        const result = await this.$apollo.mutate({
          mutation: UPSERT_GROUP,
          variables: { group }
        });
        const saved = result.data && result.data.upsertMarketingSourceGroup;
        if (!saved || !saved.id) {
          this.groupError =
            "Could not save group. Check that the name is unique and sources are not already in another group.";
          return;
        }
        this.resetGroupForm();
        this.$apollo.queries.marketingSourceGroups.refetch();
      } catch (error) {
        this.groupError = this.mutationErrorMessage(error);
      }
    },
    async deleteGroup(item) {
      if (
        !confirm(
          'Delete group "' +
            item.name +
            '"? Member sources will become ungrouped.'
        )
      ) {
        return;
      }
      try {
        await this.$apollo.mutate({
          mutation: DELETE_GROUP,
          variables: { id: item.id }
        });
        if (
          this.groupForm.id &&
          String(this.groupForm.id) === String(item.id)
        ) {
          this.resetGroupForm();
        }
        this.$apollo.queries.marketingSourceGroups.refetch();
        this.$apollo.queries.marketingSourceGoals.refetch();
      } catch (error) {
        this.groupError = this.mutationErrorMessage(error);
      }
    }
  },
  apollo: {
    caseOffices: {
      query: OFFICES_QUERY
    },
    retainedBySourceByMonth: {
      query: RETAINED_BY_SOURCE_QUERY,
      variables() {
        return {
          monthsBack: 13,
          officeId: this.selectedOfficeId
        };
      }
    },
    averageFeesBySource: {
      query: AVERAGE_FEES_BY_SOURCE_QUERY,
      variables() {
        return {
          startDate: this.feeStartDate,
          endDate: this.feeEndDate,
          officeId: this.selectedOfficeId
        };
      }
    },
    marketingSourceGoals: {
      query: GOALS_QUERY,
      variables() {
        return {
          month: this.selectedMonthDate,
          officeName: this.currentOfficeNameForGoals
        };
      }
    },
    advertisingSources: {
      query: ADVERTISING_SOURCES_QUERY
    },
    marketingSourceGroups: {
      query: GROUPS_QUERY
    }
  }
};
</script>

<style scoped>
.nested-sources-table {
  background: #fafafa;
}
::v-deep tr.group-row {
  font-weight: 500;
}
</style>
